import {
  createParser,
  createStreamingParser,
  type ElementNode,
  type Library,
  type LibraryJSONSchema,
  type ParseResult,
} from "@openuidev/lang-core";
import { z } from "zod";
import { widgetLibrary } from "./widgets";

// Validation replaces OpenUI Gateway's managed validation: model output is checked
// against our library on our servers. Invalid output never reaches the child as a
// broken widget; it degrades to text (ADR-0014). A repair round-trip through our own
// orchestrator can be added later (workstream 3), never through OpenUI Autofix.

let cachedSchema: ReturnType<ReturnType<typeof widgetLibrary>["toJSONSchema"]> | undefined;

/** The library's JSON schema, built on first use (see `widgetLibrary`). */
function schema() {
  cachedSchema ??= widgetLibrary().toJSONSchema();
  return cachedSchema;
}

export interface WidgetIssue {
  readonly component: string;
  readonly path: string;
  readonly message: string;
}

export type ValidationOutcome =
  | { readonly kind: "valid"; readonly result: ParseResult }
  | { readonly kind: "fallback"; readonly reason: string; readonly issues: readonly WidgetIssue[] };

/** Validates a complete OpenUI Lang program. Never throws. */
export function validateWidgetProgram(source: string): ValidationOutcome {
  return validateProgram(widgetLibrary(), schema(), "Stack", source);
}

/** Validates a complete program against `library`, rooted at `root`. Never throws. */
export function validateProgram(
  library: Library,
  librarySchema: LibraryJSONSchema,
  root: string,
  source: string,
): ValidationOutcome {
  let result: ParseResult;
  try {
    result = createParser(librarySchema, root).parse(source);
  } catch (error) {
    return { kind: "fallback", reason: `parse failed: ${describe(error)}`, issues: [] };
  }
  return judge(library, result);
}

/**
 * Streaming validation: feed the text received so far, render partial trees as they
 * arrive, and call `finish` once the stream ends for the final verdict.
 */
export function createWidgetStream() {
  const parser = createStreamingParser(schema(), "Stack");
  return {
    update(textSoFar: string): ParseResult {
      return parser.set(textSoFar);
    },
    finish(fullText: string): ValidationOutcome {
      try {
        return judge(widgetLibrary(), parser.set(fullText, true));
      } catch (error) {
        return { kind: "fallback", reason: `parse failed: ${describe(error)}`, issues: [] };
      }
    },
  };
}

function judge(library: Library, result: ParseResult): ValidationOutcome {
  const issues: WidgetIssue[] = result.meta.errors
    .filter((e) => e.severity !== "warning")
    .map(({ component, path, message }) => ({ component, path, message }));
  if (result.root === null) {
    return { kind: "fallback", reason: "no root widget", issues };
  }
  // The parser checks structure (known components, arity, types) but not every Zod
  // refinement (lengths, ranges). Re-check each element's props with its own schema.
  checkProps(library, result.root, issues);
  if (issues.length > 0 || result.meta.unresolved.length > 0 || result.meta.incomplete) {
    return { kind: "fallback", reason: "invalid widget program", issues };
  }
  return { kind: "valid", result };
}

function checkProps(library: Library, node: ElementNode, issues: WidgetIssue[]): void {
  const component = library.components[node.typeName];
  if (component === undefined) {
    issues.push({ component: node.typeName, path: "", message: "unknown widget" });
    return;
  }
  // Library entries are typed loosely (`DefinedComponent<any>`); every prop schema is a Zod object.
  const parsed = z.safeParse(component.props as z.core.$ZodType, node.props);
  if (!parsed.success) {
    // Child widgets are validated on their own below; their slot schemas (`.ref`)
    // describe a different runtime shape than the parser's ElementNode.
    const ownIssues = parsed.error.issues.filter((i) => !pointsIntoElement(node.props, i.path));
    for (const issue of ownIssues) {
      issues.push({
        component: node.typeName,
        path: `/${issue.path.map(String).join("/")}`,
        message: issue.message,
      });
    }
  }
  for (const value of Object.values(node.props)) {
    for (const child of Array.isArray(value) ? (value as unknown[]) : [value]) {
      if (isElement(child)) checkProps(library, child, issues);
    }
  }
}

function pointsIntoElement(props: unknown, path: readonly PropertyKey[]): boolean {
  let current: unknown = props;
  for (const key of path) {
    if (isElement(current)) return true;
    if (typeof current !== "object" || current === null) return false;
    current = (current as Record<PropertyKey, unknown>)[key];
  }
  return isElement(current);
}

function isElement(value: unknown): value is ElementNode {
  return (
    typeof value === "object" &&
    value !== null &&
    (value as { type?: unknown }).type === "element" &&
    typeof (value as { typeName?: unknown }).typeName === "string"
  );
}

function describe(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
